/**
 * View-tab ribbon handlers. Layout, plane and display options live in viewerStore;
 * panel toggles stay with the shell.
 */

import { registerAction } from "@/shell/actions";
import { appActions, useAppStore } from "@/store/appStore";
import { findNode, useViewerStore } from "./viewerStore";
import { captureAllViewports, snapshotFileName } from "./snapshot";
import type { CaseManifest, CaseNode } from "@/ipc/contract";
import type { LayoutMode, ViewPlane } from "./types";

function pressed(payload: unknown): boolean | undefined {
  if (payload && typeof payload === "object" && "pressed" in payload) {
    return Boolean((payload as { pressed?: boolean }).pressed);
  }
  return undefined;
}

function firstImageId(): string | null {
  const manifest = useAppStore.getState().currentCase;
  return manifest?.images[0]?.id ?? null;
}

export const VIEWER_ACTIONS: Record<string, (payload?: unknown) => void> = {
  "view.layout.single": () => useViewerStore.getState().setLayout("single" satisfies LayoutMode),
  "view.layout.grid": () => useViewerStore.getState().setLayout("2x2"),
  "view.layout.mpr": () => useViewerStore.getState().setLayout("mpr"),
  "view.layout.compare": () => useViewerStore.getState().setLayout("compare"),
  "view.layout.menu": () => appActions.setActiveTab("view"),

  "view.orientation.axial": () => useViewerStore.getState().setPrimaryPlane("axial" satisfies ViewPlane),
  "view.orientation.sagittal": () => useViewerStore.getState().setPrimaryPlane("sagittal"),
  "view.orientation.coronal": () => useViewerStore.getState().setPrimaryPlane("coronal"),
  "view.reset": () => {
    const store = useViewerStore.getState();
    store.setLayout("2x2");
    store.setPrimaryPlane("axial");
    store.setOption("overlayOpacity", 0.7);
    store.setOption("baseColormap", "gray");
  },

  "view.overlay.toggle": (payload) => {
    useViewerStore.getState().setOption("overlayVisible", pressed(payload) ?? true);
  },
  "view.crosshairs.toggle": (payload) => {
    useViewerStore.getState().setOption("crosshair", pressed(payload) ?? true);
  },
  "view.linkViews.toggle": (payload) => {
    useViewerStore.getState().setOption("linked", pressed(payload) ?? true);
  },

  "view.opacity.25": () => useViewerStore.getState().setOption("overlayOpacity", 0.25),
  "view.opacity.50": () => useViewerStore.getState().setOption("overlayOpacity", 0.5),
  "view.opacity.70": () => useViewerStore.getState().setOption("overlayOpacity", 0.7),
  "view.opacity.100": () => useViewerStore.getState().setOption("overlayOpacity", 1),

  "view.colormap.gray": () => useViewerStore.getState().setOption("baseColormap", "gray"),
  "view.colormap.warm": () => useViewerStore.getState().setOption("overlayColormap", "warm"),
  "view.colormap.cool": () => useViewerStore.getState().setOption("overlayColormap", "cool"),
  "view.colormap.jet": () => useViewerStore.getState().setOption("overlayColormap", "jet"),

  "view.autoContrast": () => {
    useViewerStore.getState().setOption("thresholdMin", 0);
    useViewerStore.getState().setOption("thresholdMax", 1);
  },
  "view.windowLevel": () => {
    appActions.setActiveTab("view");
    appActions.togglePanel("right", false);
    appActions.notify({
      title: "Window / Level",
      detail: "Drag on a viewport, or use the Inspector when no node is selected.",
      tone: "info",
    });
  },
  "view.goToCoordinate": () => {
    const raw = window.prompt("Voxel coordinate as i,j,k", "0,0,0");
    if (!raw) return;
    const parts = raw.split(/[,\s]+/).map(Number);
    if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) {
      appActions.notify({ title: "Could not parse coordinate", tone: "warning" });
      return;
    }
    const loc = useViewerStore.getState().location;
    if (!loc) {
      appActions.notify({ title: "Load a volume before jumping to a coordinate.", tone: "info" });
      return;
    }
    useViewerStore.getState().gotoSlice(0, parts[0]);
    useViewerStore.getState().gotoSlice(1, parts[1]);
    useViewerStore.getState().gotoSlice(2, parts[2]);
  },
  "view.zoom": () =>
    appActions.notify({ title: "Zoom", detail: "Scroll on a viewport to zoom.", tone: "info" }),
  "view.pan": () =>
    appActions.notify({ title: "Pan", detail: "Right-drag (or set the Inspector drag mode to Pan).", tone: "info" }),
  "view.snapshot": () => {
    const shot = captureAllViewports();
    if (!shot) {
      appActions.notify({
        title: "Nothing to capture",
        detail: "Open a volume in the viewer first.",
        tone: "warning",
      });
      return;
    }
    const subjectId = useAppStore.getState().currentCase?.subjectId ?? null;
    const plane = useViewerStore.getState().panes[0] ?? null;
    const name = snapshotFileName(subjectId, plane);
    const anchor = document.createElement("a");
    anchor.href = shot.dataUrl;
    anchor.download = name;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    appActions.notify({
      title: "Snapshot saved",
      detail: `${name} · ${shot.width} × ${shot.height}`,
      tone: "success",
    });
  },

  "viewer.open": () => {
    const id = firstImageId();
    if (id) {
      const node = findNode(useAppStore.getState().currentCase, id);
      if (node) useViewerStore.getState().setBaseNode(node.id);
    }
    appActions.setActiveTab("view");
  },
};

let registered = false;

export function registerViewerActions(): void {
  if (registered) return;
  registered = true;
  for (const [name, handler] of Object.entries(VIEWER_ACTIONS)) {
    registerAction(name, handler);
  }
}

/**
 * After a pipeline run, put the prediction on screen.
 *
 * Picks the mask and its base by **space**, never by position in the tree. A
 * `*_space-MNI152` mask laid over a native DWI still draws — NiiVue resamples
 * through the affine — so the mismatch is not an error anyone sees, just a
 * lesion in the wrong place. Pairing them explicitly is what keeps that from
 * happening by accident.
 *
 * Returns the node it selected, or null when the run produced no mask.
 */
export function autoOverlayPrediction(manifest: CaseManifest): CaseNode | null {
  const isNative = (n: CaseNode) => n.space === "native";
  const named = (n: CaseNode, re: RegExp) =>
    re.test(String(n.meta.fileName ?? "")) || re.test(n.relPath);

  // Stroke core first: on a combined run it is the number clinicians read.
  const overlay =
    manifest.masks.find((n) => n.derived && isNative(n) && named(n, /stroke-mask/i)) ??
    manifest.masks.find((n) => n.derived && isNative(n) && named(n, /hp-mask/i)) ??
    manifest.masks.find((n) => n.derived && named(n, /stroke-mask|hp-mask/i));
  if (!overlay) return null;

  const sameSpace = manifest.images.filter((n) => n.space === overlay.space);
  const base =
    // The preprocessed DWI, not the normalised copy: `desc-norm` is scaled for
    // the model's input and looks wrong to a reader.
    sameSpace.find((n) => n.meta.modality === "DWI" && !/desc-norm/i.test(n.relPath)) ??
    sameSpace.find((n) => !/desc-norm/i.test(n.relPath)) ??
    sameSpace[0] ??
    manifest.images[0];

  const viewer = useViewerStore.getState();
  if (base) viewer.setBaseNode(base.id);
  viewer.setOverlayNodes([overlay.id]);
  useViewerStore.setState({
    overlayVisible: true,
    // A flat red, not the `warm` heat ramp: the mask is binary, and a gradient
    // would read as a confidence the model never produced.
    overlayColormap: "red",
    overlayOpacity: 0.55,
    // 0.5 keeps a probability map, if one ever lands here, at its 50% contour.
    thresholdMin: 0.5,
    thresholdMax: 1,
  });
  return overlay;
}
